'use client';

import { useRouter } from '@bprogress/next/app';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { UiAuthInputCode } from '@/fo/ui/auth/input/code';
import { UiKitButtonBack } from '@/fo/ui/kit/button/back';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputBase } from '@/fo/ui/kit/input/base';
import { UiKitInputPassword } from '@/fo/ui/kit/input/password';
import { toastBase } from '@/fo/ui/kit/toast/base';
import { useForm } from '@/fo/ui/mod/form/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { phoneMask } from '@/fo/util/format-phone-number';

import { registerByPhone, sendAuthCode } from '../api/registration';
import { Agreements } from '../components/Agreements/Agreements';

import { styles } from './FeatureAuthFormRegistration.styles';
import { Data, formAction } from './formAction';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FeatureAuthFormRegistrationProps {
  children?: React.ReactNode;
  color?: Variants['color'];
  onClick?: NativeElement['onClick'];
  agreementSales?: string;
  agreementPrivacy?: string;
}

export function FeatureAuthFormRegistration({
  color,
  agreementSales,
  agreementPrivacy,
  ...props
}: FeatureAuthFormRegistrationProps) {
  const slots = styles({ color });
  const t = useTranslations();
  const router = useRouter();
  const { action, getFieldProps, pending, state } = useForm(formAction);
  const [formStep, setFormStep] = useState<'registration' | 'authCode'>(
    'registration'
  );

  const formData = state.data as Data;

  useEffect(() => {
    if (state.status === 'success' && !pending) {
      setFormStep('authCode');
    }
  }, [state, pending]);

  return (
    <div
      className={slots.base()}
      {...props}
    >
      {formStep === 'registration' && (
        <form action={action}>
          <div className={slots.fields()}>
            <UiKitInputBase
              label={t('form.label.name')}
              {...getFieldProps('name')}
              disabled={pending}
            />
            <UiKitInputBase
              label={t('form.label.email')}
              {...getFieldProps('email')}
              disabled={pending}
            />
            <UiKitInputBase
              label={t('form.label.phone')}
              {...getFieldProps('phone')}
              disabled={pending}
              mask={phoneMask}
            />
            <UiKitInputPassword
              label={t('form.label.password')}
              {...getFieldProps('password')}
              disabled={pending}
              helperText={
                getFieldProps('password').helperText ||
                t('form.label.passwordHelperText')
              }
            />
            <UiKitInputPassword
              label={t('form.label.passwordConfirm')}
              {...getFieldProps('passwordConfirm')}
              disabled={pending}
            />
          </div>

          <Agreements
            getFieldProps={getFieldProps}
            pending={pending}
            agreementSales={agreementSales}
            agreementPrivacy={agreementPrivacy}
          />

          <UiKitButtonBase
            type="submit"
            className={slots.button()}
            loading={pending}
          >
            <span>{t('form.button.registration')}</span>
          </UiKitButtonBase>
        </form>
      )}
      {formStep === 'authCode' && (
        <>
          <div>
            <UiKitButtonBack onClick={() => setFormStep('registration')} />
          </div>
          <UiAuthInputCode
            phoneNumber={(state.data?.phone as string) ?? ''}
            onSendCode={async (code) => {
              const response = await registerByPhone(
                (state.data?.phone as string) ?? '',
                code,
                formData
              );

              if (response.status === 'success') {
                setFormStep('registration');
                const targetUrl = utilEventAuthModal.getRedirectUrl();

                if (targetUrl) {
                  utilEventAuthModal.close();
                  window.location.assign(targetUrl);
                  return response;
                }

                utilEventAuthModal.close();
                router.push('/account');
              }

              if (response.status === 'error' && response.toastMessage) {
                toastBase(response.toastMessage, { status: 'error' });
              }

              return response;
            }}
            onResend={() => sendAuthCode((state.data?.phone as string) ?? '')}
          />
        </>
      )}
    </div>
  );
}
